// ProfilePage.js
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import MapPicker from "../components/Registration/MapPicker";
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Divider,
  Button,
  TextField,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Breadcrumbs,
  Link,
} from "@mui/material";
import {
  PhotoCameraOutlined as PhotoIcon,
  // Edit as EditIcon,
} from "@mui/icons-material";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
 import axios from "axios";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";

/* ============================
   ZOD SCHEMA
   ============================ */
const ProfileSchema = z
  .object({
    firstName: z.string().min(2, "First name must be at least 2 characters"),
    lastName: z.string().min(2, "Last name must be at least 2 characters"),
    gender: z.string().optional(),
    email: z.string().email("Invalid email"),
   mobile: z
  .string()
  .regex(/^[6-9]\d{9}$/, "Invalid mobile number"),
    password: z.string().optional(),
    confirmPassword: z.string().optional(),
    addressLine1: z.string().optional(),
    addressLine2: z.string().optional(),
    pincode: z
  .string()
  .regex(/^\d{6}$/, "Invalid PIN code")
  .optional()
  .or(z.literal("")),
    city: z.string().optional(),
    state: z.string().optional(),
    location: z
  .object({
    type: z.literal("Point"),
    coordinates: z.array(z.number()).length(2),
  })
  .optional(),
    addressType: z.string().optional(),
    otherType: z.string().optional(),
  })
  .refine(
    (data) =>
      !data.password || data.password === data.confirmPassword,
    {
      message: "Passwords do not match",
      path: ["confirmPassword"],
    }
  );

/* ============================
   PASSWORD STRENGTH
   ============================ */
const getPasswordStrength = (password) => {
  let score = 0;
  if (!password) return { score: 0, color: "#d32f2f" };
  if (password.length >= 6) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[\W_]/.test(password)) score++;
  return { score, color: score >= 4 ? "#2e7d32" : "#f9a825" };
};

const states = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
];

const ProfilePage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [profilePic, setProfilePic] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [needsPassword, setNeedsPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0 });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const geocodeAddress = async (address) => {
  try {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    const res = await axios.get(
      "https://maps.googleapis.com/maps/api/geocode/json",
      {
        params: {
          address,
          key: apiKey,
        },
      }
    );

    if (res.data.results.length > 0) {
      const loc = res.data.results[0].geometry.location;

      return {
        type: "Point",
        coordinates: [loc.lng, loc.lat],
      };
    }

    return null;
  } catch (err) {
    console.error("Geocode error", err);
    return null;
  }
};
  const {
  register,
  control,
  setValue,
  getValues,
  handleSubmit,
  watch,
  formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(ProfileSchema),
    mode: "onBlur",
    defaultValues: {
      firstName: "",
      lastName: "",
      gender: "",
      email: "",
      mobile: "",
      password: "",
      confirmPassword: "",
      addressLine1: "",
      addressLine2: "",
      pincode: "",
      city: "",
      state: "",
      location: {
        type: "Point",
        coordinates: [0, 0],
      },
      longitude: "",
      addressType: "Home",
      otherType: "",
    },
  });

  const password = watch("password");
  const location = watch("location");
  const pincode = watch("pincode");
  const city = watch("city");
  const state = watch("state");

const initialLocation = useMemo(() => {
  if (
    location?.coordinates &&
    location.coordinates.length === 2 &&
    location.coordinates[0] !== 0 &&
    location.coordinates[1] !== 0
  ) {
    return {
      latitude: location.coordinates[1],
      longitude: location.coordinates[0],
    };
  }

  return null;
}, [location]);

  const showSnack = (msg, severity = "success") =>
    setSnack({ open: true, message: msg, severity });

  useEffect(() => {
  setPasswordStrength(getPasswordStrength(password || ""));
}, [password]);
  /* ---------------- LOAD USER ---------------- */
  useEffect(() => {
    const savedUser = JSON.parse(localStorage.getItem("user") || "{}");

    if (!savedUser.username) {
      navigate("/login");
      return;
    }

    api
      .get(`/users/profile/${savedUser.username}`)
      .then((res) => {
        setIsLoading(false);
        const u = res.data.user;

        localStorage.setItem("user", JSON.stringify(u));

        setNeedsPassword(!u.hasPassword); // 🔑 AMAZON LOGIC
        // setNeedsPassword(!u.password); // 🔑 AMAZON LOGIC
        setValue("firstName", u.firstName || "");
        setValue("lastName", u.lastName || "");
        setValue("gender", u.gender || "");
        setValue("mobile", u.mobile || "");
        setValue("email", u.email || u.username);

        if (u.profilePic) setProfilePic(u.profilePic);

        if (u.addresses?.length) {
          const a = u.addresses.find((x) => x.isDefault) || u.addresses[0];
          setValue("addressLine1", a.addressLine1 || "");
          setValue("addressLine2", a.addressLine2 || "");
          setValue("pincode", a.pincode || "");
          setValue("city", a.city || "");
          setValue("state", a.state || "");
          setValue("addressType", a.addressType || "Home");
          setValue("otherType", a.otherType || "");
          if (a.location?.coordinates?.length === 2) {
            setValue("location", a.location);
          }
        }
      })
      .catch(() => {
        setIsLoading(false);
        navigate("/login");
      });
  }, [navigate, setValue]);

  /* ---------------- IMAGE UPLOAD ---------------- */
  const convertToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const base64 = await convertToBase64(file);
    setProfilePic(base64);
  };

  useEffect(() => {
  const timer = setTimeout(async () => {
    const address = [
      getValues("addressLine1"),
      getValues("addressLine2"),
      getValues("city"),
      getValues("state"),
      getValues("pincode"),
      "India",
    ]
      .filter(Boolean)
      .join(", ");

    if (address.length > 10) {
      const geo = await geocodeAddress(address);

      if (geo) {
        setValue("location", geo);
      }
    }
  }, 1000);

  return () => clearTimeout(timer);
}, [
  watch("addressLine1"),
  watch("addressLine2"),
  watch("city"),
  watch("state"),
  watch("pincode"),
]);

  /* ============================
     PINCODE LOOKUP
     ============================ */
  useEffect(() => {
  const fetchCityState = async (pin) => {
    if (pin.length !== 6) return;

    try {
      const res = await axios.get(
        `https://api.postalpincode.in/pincode/${pin}`
      );

      if (res.data[0].Status === "Success") {
        const info = res.data[0].PostOffice[0];

        // ✅ ONLY SET IF EMPTY
        if (!getValues("city")) {
          setValue("city", info.District);
        }

        if (!getValues("state")) {
          setValue("state", info.State);
        }
      } else {
        showSnack("Invalid Pincode", "error");
      }
    } catch {
      showSnack("Couldn't fetch pincode info", "error");
    }
  };

  if (pincode?.length === 6) {
    fetchCityState(pincode);
  }
}, [pincode, setValue, getValues]);

  /* ---------------- SUBMIT ---------------- */
 const onSubmit = async (data) => {
  try {
    const existingUser = JSON.parse(localStorage.getItem("user"));

    /* 🔐 STEP 1: SET PASSWORD (FIRST TIME ONLY) */
    if (needsPassword) {
      if (!data.password || data.password.length < 6) {
        showSnack("Password must be at least 6 characters", "error");
        return;
      }

      if (data.password !== data.confirmPassword) {
        showSnack("Passwords do not match", "error");
        return;
      }

      const passRes = await api.post("/auth/set-password", {
        username: existingUser.username,
        password: data.password,
      });

      /* ✅ SAVE TOKEN IMMEDIATELY */
      localStorage.setItem("token", passRes.data.token);
      localStorage.setItem("user", JSON.stringify(passRes.data.user));
    }

    let finalLocation = data.location;

const hasValidCoordinates =
  finalLocation?.coordinates &&
  finalLocation.coordinates[0] !== 0 &&
  finalLocation.coordinates[1] !== 0;

if (!hasValidCoordinates) {
  const fullAddress = [
    data.addressLine1,
    data.addressLine2,
    data.city,
    data.state,
    data.pincode,
    "India",
  ]
    .filter(Boolean)
    .join(", ");

  const geoLocation = await geocodeAddress(fullAddress);

  if (geoLocation) {
    finalLocation = geoLocation;
  }
}

    /* 🧾 STEP 2: SAVE PROFILE */
    const payload = {
      username: existingUser.username,
      firstName: data.firstName,
      lastName: data.lastName,
      gender: data.gender,
      email: data.email,
      mobile: data.mobile,
      profilePic,
      addresses: [
        {
          addressLine1: data.addressLine1,
          addressLine2: data.addressLine2,
          city: data.city,
          state: data.state,
          country: "India",
          pincode: data.pincode,
          addressType: data.addressType,
          otherType: data.otherType || "",
          location: finalLocation,
          isDefault: true,
        },
      ],
    };

    const res = await api.put("/users/profile", payload);

    /* 🔄 UPDATE USER AFTER PROFILE SAVE */
    localStorage.setItem("user", JSON.stringify(res.data.user));

    showSnack("Profile updated successfully");
    setTimeout(() => navigate("/"), 800);

  } catch (err) {
    console.error(err);
    showSnack("Failed to update profile", "error");
  }
};


const handleLogout = () => {
  localStorage.removeItem("user");
  localStorage.removeItem("token");
  navigate("/login");
};

  /* ============================
     RENDER HELPERS
     ============================ */
  const renderModernField = (label, field, required = false) => (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        variant="body2"
        fontWeight={500}
        sx={{ mb: 1, color: "#555" }}
      >
        {label} {required && <span style={{ color: "#ff9900" }}>*</span>}
      </Typography>
      {field}
    </Box>
  );

  const renderModernTextField = (props) => (
    <TextField
      {...props}
      fullWidth
      variant="outlined"
      size="small"
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
          transition: "all 0.2s ease",
          "&:hover": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#ff9900",
            },
          },
          "&.Mui-focused": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#ff9900",
              borderWidth: 2,
            },
          },
        },
        "& .MuiInputLabel-root.Mui-focused": {
          color: "#ff9900",
        },
      }}
    />
  );

  const modernButtonStyle = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 600,
    px: 3,
    py: 1,
    transition: "all 0.2s ease",
    boxShadow: "0 2px 8px rgba(255,153,0,0.2)",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 4px 12px rgba(255,153,0,0.3)",
    },
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress sx={{ color: "#ff9900" }} />
      </Box>
    );
  }

  /* ============================
     UI RENDER
     ============================ */
  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        py: 4,
      }}
    >
      <Box
        sx={{
          maxWidth: 700,
          mx: "auto",
          px: 2,
        }}
      >
        {/* Breadcrumb Navigation */}
        <Box sx={{ mb: 3 }}>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link
              underline="hover"
              color="inherit"
              href="/customer/dashboard"
              onClick={(e) => {
                e.preventDefault();
                navigate("/customer/dashboard");
              }}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
              Customer Dashboard
            </Link>
            <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
              Your Address
            </Typography>
          </Breadcrumbs>
        </Box>

        <Paper elevation={0} sx={{ p: 4, borderRadius: 2 }}>
          {/* PROFILE PICTURE SECTION */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Box
              sx={{
                position: "relative",
                width: 120,
                height: 120,
                mx: "auto",
                mb: 2,
              }}
            >
              <Avatar
                src={profilePic || ""}
                sx={{
                  width: "100%",
                  height: "100%",
                  bgcolor: "#ff9900",
                  fontSize: 48,
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(255,153,0,0.3)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "scale(1.05)",
                  },
                }}
                onClick={() => fileInputRef.current.click()}
              >
                {!profilePic && "P"}
              </Avatar>
              <Box
                sx={{
                  position: "absolute",
                  bottom: -2,
                  right: -2,
                  bgcolor: "#ff9900",
                  borderRadius: "50%",
                  p: 0.8,
                  cursor: "pointer",
                }}
                onClick={() => fileInputRef.current.click()}
              >
                <PhotoIcon sx={{ color: "#fff", fontSize: 20 }} />
              </Box>
            </Box>

            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              hidden
              onChange={handleImageUpload}
            />

            <Typography variant="caption" sx={{ color: "#999" }}>
              Click to upload profile picture
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* PERSONAL INFO */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Personal Information
            </Typography>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 3 }}>
              {renderModernField(
                "First Name",
                renderModernTextField({
                  placeholder: "First name",
                  ...register("firstName"),
                  error: !!errors.firstName,
                  helperText: errors.firstName?.message,
                }),
                true
              )}

              {renderModernField(
                "Last Name",
                renderModernTextField({
                  placeholder: "Last name",
                  ...register("lastName"),
                  error: !!errors.lastName,
                  helperText: errors.lastName?.message,
                }),
                true
              )}
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 3 }}>
              {renderModernField(
                "Gender",
                <FormControl fullWidth size="small" sx={{ mb: 0 }}>
                  <InputLabel sx={{ color: "#666" }}>Select Gender</InputLabel>
                  <Controller
  name="gender"
  control={control}
  render={({ field }) => (
    <Select
      {...field}
      label="Select Gender"
      sx={{
        borderRadius: 2,
      }}
    >
      <MenuItem value="Male">Male</MenuItem>
      <MenuItem value="Female">Female</MenuItem>
      <MenuItem value="Other">Other</MenuItem>
    </Select>
  )}
/>
                  {/* <Select
                    {...register("gender")}
                    label="Select Gender"
                    sx={{
                      borderRadius: 2,
                      "& .MuiOutlinedInput-root": {
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#ff9900",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#ff9900",
                        },
                      },
                    }}
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select> */}
                </FormControl>
              )}

              {renderModernField(
                "Mobile",
                renderModernTextField({
                  placeholder: "10 digit mobile",
                  ...register("mobile"),
                  error: !!errors.mobile,
                  helperText: errors.mobile?.message,
                }),
                true
              )}
            </Box>

            {renderModernField(
              "Email",
              renderModernTextField({
                placeholder: "your@email.com",
                type: "email",
                disabled: true,
                ...register("email"),
                error: !!errors.email,
                helperText: errors.email?.message,
              }),
              true
            )}

            {/* PASSWORD SECTION */}
            {needsPassword && (
              <>
                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Set Security Password
                </Typography>

                {renderModernField(
                  "Password",
                  <Box>
                    {renderModernTextField({
                      placeholder: "Enter password",
                      type: showPassword ? "text" : "password",
                      ...register("password", {
                        required: "Password is required",
                        minLength: { value: 6, message: "Minimum 6 characters" },
                      }),
                      error: !!errors.password,
                      helperText: errors.password?.message,
                      InputProps: {
                        endAdornment: (
                          <Button
                            size="small"
                            onClick={() => setShowPassword(!showPassword)}
                            sx={{ mr: -1 }}
                          >
                            {showPassword ? "Hide" : "Show"}
                          </Button>
                        ),
                      },
                    })}
                    {password && (
                      <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            height: 6,
                            flex: 1,
                            bgcolor: "#e0e0e0",
                            borderRadius: 1,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              width: `${(passwordStrength.score / 5) * 100}%`,
                              bgcolor: passwordStrength.color,
                              transition: "width 0.3s",
                            }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ color: passwordStrength.color }}>
                          {passwordStrength.score <= 2
                            ? "Weak"
                            : passwordStrength.score <= 3
                            ? "Fair"
                            : "Strong"}
                        </Typography>
                      </Box>
                    )}
                  </Box>,
                  true
                )}

                {renderModernField(
                  "Confirm Password",
                  renderModernTextField({
                    placeholder: "Confirm password",
                    type: showConfirmPassword ? "text" : "password",
                    ...register("confirmPassword", {
                      required: "Please confirm password",
                    }),
                    error: !!errors.confirmPassword,
                    helperText: errors.confirmPassword?.message,
                    InputProps: {
                      endAdornment: (
                        <Button
                          size="small"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          sx={{ mr: -1 }}
                        >
                          {showConfirmPassword ? "Hide" : "Show"}
                        </Button>
                      ),
                    },
                  }),
                  true
                )}
              </>
            )}

            {/* ADDRESS SECTION */}
            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Delivery Address
            </Typography>

<MapPicker
  setValue={setValue}
  initialLocation={initialLocation}
/>

            {renderModernField(
              "Address Line 1",
              renderModernTextField({
                placeholder: "Building, street, etc.",
                multiline: true,
                rows: 2,
                ...register("addressLine1"),
              })
            )}

            {renderModernField(
              "Address Line 2",
              renderModernTextField({
                placeholder: "Additional address details (optional)",
                ...register("addressLine2"),
              })
            )}

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 3 }}>
              {renderModernField(
                "City",
                renderModernTextField({
                  placeholder: "City",
                  ...register("city"),
                })
              )}

              {renderModernField(
                "PIN Code",
                renderModernTextField({
                  placeholder: "PIN code",
                  ...register("pincode"),
                })
              )}
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 3 }}>
              {renderModernField(
                "State",
                <FormControl fullWidth size="small">
                  <InputLabel>Select State</InputLabel>

                  <Controller
                    name="state"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Select State"
                        sx={{
                          borderRadius: 2,
                        }}
                      >
                        {states.map((state) => (
                          <MenuItem key={state} value={state}>
                            {state}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              )}

              {renderModernField(
                "Address Type",
                <FormControl fullWidth size="small">
                    <InputLabel>Address Type</InputLabel>

                    <Controller
                      name="addressType"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          label="Address Type"
                          sx={{
                            borderRadius: 2,
                          }}
                        >
                          <MenuItem value="Home">Home</MenuItem>
                          <MenuItem value="Work">Work</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </Select>
                      )}
                    />
                  </FormControl>
              )}
            </Box>

            {watch("addressType") === "Other" && (
              <Box sx={{ mb: 3 }}>
                {renderModernField(
                  "Specify Address Type",
                  renderModernTextField({
                    placeholder: "e.g., Hostel, Dorm",
                    ...register("otherType"),
                  })
                )}
              </Box>
            )}

            {/* BUTTONS */}
            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  ...modernButtonStyle,
                  bgcolor: "#ff9900",
                  color: "#000",
                  flex: 1,
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} sx={{ color: "#ff9900" }} />
                ) : (
                  "Save Profile"
                )}
              </Button>

              <Button
                variant="outlined"
                onClick={handleLogout}
                sx={{
                  ...modernButtonStyle,
                  color: "#ff9900",
                  borderColor: "#ff9900",
                  flex: 1,
                }}
              >
                Logout
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity} variant="filled">
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfilePage;
